import React, { useState } from 'react'
import {
  HiOutlineUser, HiOutlineClock, HiOutlineCheckCircle,
  HiOutlineEye, HiOutlinePlay, HiOutlinePause, HiOutlineDocument,
  HiOutlineUsers, HiOutlineArrowDownTray
} from 'react-icons/hi2'
import { getSecurityLevelConfig, getMessageScopeConfig } from '../../config/messageSecurity'
import { formatDistanceToNow } from 'date-fns'
import { useAuth } from '../../contexts/AuthContext'
import { tokenStorage } from '../../utils/crypto'
import BlockedAction from '../common/BlockedAction'

const MessageCard = ({ message, onMarkAsRead, onPlay, playingMessageId, isPlaying }) => {
  const { user } = useAuth()
  const [showReaders, setShowReaders] = useState(false)
  const isSender = user && message.created_by === user.id
  const securityConfig = getSecurityLevelConfig(message.security_level)
  const scopeConfig = getMessageScopeConfig(message.message_scope)

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return dateString
    }
  }

  const isBroadcast = message.message_scope === 'broadcast'

  const handleDownloadText = async () => {
    if (!message.secure_download_url) return
    try {
      const token = tokenStorage.getToken()
      if (!token) return
      const response = await fetch(message.secure_download_url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      })
      if (!response.ok) return
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = url
      link.download = message.file_name || `${message.title || 'message'}.txt`
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading text message:', err)
    }
  }
  
  return (
    <div className={`${
      isBroadcast 
        ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-400 shadow-lg' 
        : isSender 
          ? 'ml-auto bg-white border-2 border-cyan-500 shadow-md' 
          : 'mr-auto bg-white border border-gray-200'
    } max-w-2xl rounded-2xl p-5 hover:shadow-xl transition-all duration-200 ${!isSender && !message.is_read ? 'border-l-4 border-l-cyan-500' : ''}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${isSender ? 'bg-gradient-to-r from-cyan-500 to-cyan-600' : 'bg-gradient-to-r from-cyan-500 to-cyan-600'} rounded-full flex items-center justify-center`}>
            <HiOutlineUser className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className={`font-semibold ${isSender ? 'text-cyan-600' : 'text-gray-900'}`}>
              {isSender ? 'You' : message.created_by_name}
            </h3>
            <div className={`flex items-center gap-2 text-sm ${isSender ? 'text-gray-600' : 'text-gray-500'}`}>
              <HiOutlineClock className="w-4 h-4" />
              <span>{formatDate(message.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Security Badge */}
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${securityConfig.bgColor} ${securityConfig.textColor}`}>
            {securityConfig.icon} {securityConfig.label.split(' - ')[0]}
          </span>
        </div>
      </div>

      {/* Broadcast Badge - Prominent */}
      {message.message_scope === 'broadcast' && (
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-md animate-pulse">
            📢 BROADCAST MESSAGE
          </span>
          <span className="text-xs text-orange-600 font-medium">
            Sent to all workspace members
          </span>
        </div>
      )}

      {/* Title */}
      {message.title && (
        <div className="mb-2">
          <h4 className="font-semibold text-gray-900 text-base">{message.title}</h4>
        </div>
      )}

      {/* Context Info */}
      <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
        {message.case_title && (
          <div className="flex items-center gap-1">
            <HiOutlineDocument className="w-4 h-4" />
            <span>Case: {message.case_title}</span>
          </div>
        )}
        {message.client_name && (
          <div className="flex items-center gap-1">
            <HiOutlineUser className="w-4 h-4" />
            <span>Client: {message.client_name}</span>
          </div>
        )}
        {message.session_title && (
          <div className="flex items-center gap-1">
            <HiOutlineClock className="w-4 h-4" />
            <span>Session: {message.session_title}</span>
          </div>
        )}
      </div>

      {/* Message Content */}
      {message.type === 'text' && message.content && (
        <div className={`${isSender ? 'bg-cyan-50' : 'bg-gray-50'} rounded-lg p-4 mb-3 relative group`}>
          <p className="text-gray-700 whitespace-pre-wrap break-words overflow-wrap-anywhere">{message.content}</p>
          {message.secure_download_url && (
            <BlockedAction action="download" badgeSize="sm" className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={handleDownloadText}
                className="p-1.5 rounded-md text-gray-400 hover:text-cyan-600 hover:bg-white/70"
                title="Download as .txt"
              >
                <HiOutlineArrowDownTray className="w-4 h-4" />
              </button>
            </BlockedAction>
          )}
        </div>
      )}

      {/* Voice Message */}
      {message.type === 'voice' && message.voice_stream_url && (
        <div className={`${isSender ? 'bg-cyan-50 border border-cyan-200' : 'bg-gray-50 border border-gray-200'} rounded-lg p-4 mb-3`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BlockedAction action="stream_voice" badgeSize="sm">
                <button
                  onClick={() => onPlay && onPlay(message)}
                  className="w-10 h-10 bg-cyan-500 hover:bg-cyan-600 rounded-full flex items-center justify-center transition-colors"
                  title={playingMessageId === message.id && isPlaying ? 'Pause' : 'Play'}
                >
                  {playingMessageId === message.id && isPlaying ? (
                    <HiOutlinePause className="w-5 h-5 text-white" />
                  ) : (
                    <HiOutlinePlay className="w-5 h-5 text-white" />
                  )}
                </button>
              </BlockedAction>
              <div>
                <p className={`text-sm font-medium ${isSender ? 'text-cyan-700' : 'text-gray-700'}`}>Voice Message</p>
                <p className={`text-xs ${isSender ? 'text-cyan-600' : 'text-gray-600'}`}>
                  {playingMessageId === message.id && isPlaying ? 'Playing...' : 'Click to play'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        {isSender ? (
          /* Sender View - Show who read the message */
          <div className="w-full">
            <button
              onClick={() => setShowReaders(!showReaders)}
              className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
            >
              <HiOutlineUsers className="w-4 h-4" />
              <span>{message.read_count} {message.read_count === 1 ? 'person' : 'people'} read this</span>
            </button>
            
            {/* Readers List */}
            {showReaders && message.read_by_users && message.read_by_users.length > 0 && (
              <div className="mt-3 bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-cyan-700 mb-2">Read by:</p>
                <div className="space-y-2">
                  {message.read_by_users.map((reader) => (
                    <div key={reader.id} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center">
                          <HiOutlineUser className="w-3 h-3 text-cyan-600" />
                        </div>
                        <span className="text-gray-700 font-medium">{reader.name}</span>
                      </div>
                      {reader.read_at && (
                        <span className="text-gray-600">
                          {formatDistanceToNow(new Date(reader.read_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {showReaders && (!message.read_by_users || message.read_by_users.length === 0) && (
              <p className="mt-2 text-xs text-gray-500 italic">No one has read this message yet</p>
            )}
          </div>
        ) : (
          /* Recipient View - Show mark as read button */
          <>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <HiOutlineEye className="w-4 h-4" />
              <span>{message.read_count} read</span>
            </div>

            {!message.is_read && (
              <button
                onClick={() => onMarkAsRead && onMarkAsRead(message.id)}
                className="text-sm text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1"
              >
                <HiOutlineCheckCircle className="w-4 h-4" />
                Mark as Read
              </button>
            )}

            {message.is_read && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <HiOutlineCheckCircle className="w-4 h-4" />
                Read
              </span>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default MessageCard

